import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import {
  CameraFill,
  Image,
  EyeFill,
  EyeSlashFill,
} from "react-bootstrap-icons";
import { Autocomplete, TextField } from "@mui/material";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_OPERATORS_API_URL =
  "https://estbn.com/EstbnNew/public/api/portal/operators";
const ROLES_API_URL = "https://estbn.com/EstbnNew/public/api/portal/roles";
const OPERATOR_CATEGORIES_API_URL =
  "https://estbn.com/EstbnNew/public/api/portal/operator/categories";
const SYNC_ROLES_API_URL =
  "https://estbn.com/EstbnNew/public/api/portal/user/sync_roles";

const formatDateObjectToYYYYMMDD = (dateObject) => {
  if (
    !dateObject ||
    !(dateObject instanceof Date) ||
    isNaN(dateObject.getTime())
  ) {
    return "";
  }
  const year = dateObject.getFullYear();
  const month = (dateObject.getMonth() + 1).toString().padStart(2, "0");
  const day = dateObject.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeString = (str) => {
  return str ? str.toString().toLowerCase().replace(/[-_]/g, " ").trim() : "";
};

const Addmember = ({
  show,
  onHide,
  onSubmitSuccess,
  memberData,
  isDuplicating = false,
}) => {
  const { user } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    selectedRoleId: null,
    selectedRoleName: "",
    joined_at: null,
    birthDate: null,
    mobileNumber: "",
    gender: "",
    username: "",
    email: "",
    category: "",
    password: "",
    passwordConfirmation: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [roles, setRoles] = useState([]);
  const [operatorCategoriesList, setOperatorCategoriesList] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [loadingRoles, setLoadingRoles] = useState(false);

  const genderOptions = ["Male", "Female"];
  const isEditing = !!memberData && !isDuplicating;

  const resetForm = useCallback(() => {
    setFormData({
      firstName: "",
      lastName: "",
      selectedRoleId: null,
      selectedRoleName: "",
      joined_at: null,
      birthDate: null,
      mobileNumber: "",
      gender: "",
      username: "",
      email: "",
      category: "",
      password: "",
      passwordConfirmation: "",
    });
    setShowPassword(false);
    setProfileImage(null);
    setProfileImagePreview(null);
    setFormErrors({});
  }, []);

  useEffect(() => {
    const fetchRolesAndCategories = async () => {
      if (!user?.token) return;

      setLoadingRoles(true);
      try {
        const rolesResponse = await axios.get(ROLES_API_URL, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (rolesResponse.data.success) {
          const rolesData = rolesResponse.data.data.roles || [];
          setRoles(rolesData);

          if (
            (isEditing || isDuplicating) &&
            memberData &&
            rolesData.length > 0
          ) {
            const memberRoles = memberData.roles || [];
            const roleNameFromApi =
              memberRoles.length > 0 ? memberRoles[0] : memberData.role || "";

            if (roleNameFromApi) {
              const correspondingRole = rolesData.find(
                (role) =>
                  normalizeString(role.name) ===
                  normalizeString(roleNameFromApi)
              );

              if (correspondingRole) {
                setFormData((prev) => ({
                  ...prev,
                  selectedRoleId: correspondingRole.id,
                  selectedRoleName: correspondingRole.name,
                }));
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching roles", error);
        toast.error("Failed to load roles");
      } finally {
        setLoadingRoles(false);
      }

      try {
        const categoriesResponse = await axios.get(
          OPERATOR_CATEGORIES_API_URL,
          {
            headers: { Authorization: `Bearer ${user.token}` },
          }
        );
        if (categoriesResponse.data.success) {
          setOperatorCategoriesList(
            categoriesResponse.data.data.categories || []
          );
        }
      } catch (error) {
        console.error("Error fetching categories", error);
      }
    };

    if (show) {
      fetchRolesAndCategories();
    }
  }, [show, user?.token, isEditing, isDuplicating, memberData]);

  useEffect(() => {
    if (!show) {
      resetForm();
      return;
    }

    if ((isEditing || isDuplicating) && memberData) {
      let categoryValue = memberData.category;

      setFormData((prev) => ({
        ...prev,
        firstName: memberData.first_name || "",
        lastName: memberData.last_name || "",
        mobileNumber: memberData.phone_number || "",
        gender: memberData.gender ? memberData.gender.toLowerCase() : "",
        username: memberData.username || "",
        email: memberData.email || "",
        category: categoryValue || "",
        joined_at: memberData.joined_at ? new Date(memberData.joined_at) : null,
        birthDate: memberData.birthdate ? new Date(memberData.birthdate) : null,
      }));

      setProfileImagePreview(memberData.profile_photo_url || null);
      setProfileImage(null);
      setFormErrors({});
    } else if (!isEditing && !isDuplicating) {
      resetForm();
    }
  }, [show, memberData, isEditing, isDuplicating, resetForm]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setProfileImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setProfileImage(null);
      setProfileImagePreview(
        (isEditing || isDuplicating) && memberData?.profile_photo_url
          ? memberData.profile_photo_url
          : null
      );
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "First Name is required.";
    if (!formData.lastName.trim())
      newErrors.lastName = "Last Name is required.";
    if (!formData.selectedRoleId)
      newErrors.selectedRoleId = "Role is required.";
    if (!formData.joined_at || isNaN(formData.joined_at?.getTime()))
      newErrors.joined_at = "Join Date is required.";
    if (!formData.birthDate || isNaN(formData.birthDate?.getTime()))
      newErrors.birthDate = "Birth Date is required.";
    if (!formData.gender) newErrors.gender = "Gender is required.";
    if (!formData.username.trim()) newErrors.username = "Username is required.";

    if (!formData.category) newErrors.category = "Category is required.";

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile Number is required.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email address.";
    }

    if (!isEditing || (isEditing && formData.password)) {
      if (!formData.password && !isEditing) {
        newErrors.password = "Password is required.";
      } else if (formData.password && formData.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters long.";
      }

      if (formData.password) {
        if (!formData.passwordConfirmation) {
          newErrors.passwordConfirmation = "Password Confirmation is required.";
        } else if (formData.passwordConfirmation !== formData.password) {
          newErrors.passwordConfirmation = "Passwords do not match.";
        }
      }
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please correct the errors in the form.");
      return;
    }

    const token = user?.token;
    if (!token) {
      toast.error("Authentication token is missing.");
      return;
    }

    const submitFormData = new FormData();
    submitFormData.append("first_name", formData.firstName);
    submitFormData.append("last_name", formData.lastName);
    if (formData.birthDate)
      submitFormData.append(
        "birthdate",
        formatDateObjectToYYYYMMDD(formData.birthDate)
      );
    if (formData.joined_at)
      submitFormData.append(
        "joined_at",
        formatDateObjectToYYYYMMDD(formData.joined_at)
      );

    submitFormData.append("phone_number", formData.mobileNumber);
    submitFormData.append("gender", formData.gender);
    submitFormData.append("username", formData.username);
    submitFormData.append("email", formData.email);
    submitFormData.append("category", formData.category || "");

    // Always include role_id for the main endpoint if available
    if (formData.selectedRoleId) {
      submitFormData.append("role_id", formData.selectedRoleId);
    }

    if (formData.password) {
      submitFormData.append("password", formData.password);
      submitFormData.append(
        "password_confirmation",
        formData.passwordConfirmation
      );
    }

    if (profileImage) {
      submitFormData.append("profile_photo", profileImage);
    }

    let requestUrl = BASE_OPERATORS_API_URL;

    if (isEditing) {
      requestUrl = `${BASE_OPERATORS_API_URL}/${memberData.id}`;
      submitFormData.append("_method", "PATCH");
    }

    try {
      const response = await axios({
        method: "POST", // Always POST because of FormData/_method hack
        url: requestUrl,
        data: submitFormData,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      let finalOperatorData = response.data.data?.operator ||
        response.data.data || { ...memberData };

      finalOperatorData.category = formData.category;
      finalOperatorData.first_name = formData.firstName;
      finalOperatorData.last_name = formData.lastName;
      finalOperatorData.phone_number = formData.mobileNumber;
      finalOperatorData.email = formData.email;
      finalOperatorData.username = formData.username;
      finalOperatorData.gender = formData.gender;

      const userId = isEditing
        ? memberData.id
        : finalOperatorData.id || response.data.data?.id;

      // 3. Sync Roles
      if (formData.selectedRoleId && userId) {
        try {
          const syncRoleData = new FormData();
          syncRoleData.append("user_id", userId);
          syncRoleData.append("user_type", "operator");
          syncRoleData.append("role_ids[]", formData.selectedRoleId);

          await axios.post(SYNC_ROLES_API_URL, syncRoleData, {
            headers: { Authorization: `Bearer ${token}` },
          });

          const selectedRoleObj = roles.find(
            (r) => r.id === formData.selectedRoleId
          );
          if (selectedRoleObj) {
            finalOperatorData.roles = [selectedRoleObj.name];
            finalOperatorData.role = selectedRoleObj.name;
          }
        } catch (roleError) {
          console.error("Failed to sync roles", roleError);
        }
      }

      toast.success(
        response.data.message ||
          `${
            isEditing ? "Operator updated" : "New operator added"
          } successfully!`
      );

      if (onSubmitSuccess) {
        onSubmitSuccess(finalOperatorData);
      }

      onHide();
    } catch (error) {
      console.error(error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        `Failed to ${isEditing ? "update" : "add"} operator.`;
      const detailedErrors = error.response?.data?.errors;
      if (detailedErrors) {
        const errorStrings = Object.values(detailedErrors).flat();
        toast.error(`${errorMessage}: ${errorStrings.join(", ")}`);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        dialogClassName="custom-modal-width"
      >
        <Modal.Header
          closeButton
          style={{ borderBottom: "none", padding: "1.5rem 1.5rem 0.5rem" }}
        >
          <Modal.Title className="text-black h5 w-75">
            {isEditing ? "Edit Operator" : "Add New Operator"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="p-4"
          style={{ maxHeight: "100vh", overflowY: "auto" }}
        >
          <ToastContainer
            position="top-center"
            autoClose={3000}
            hideProgressBar={false}
          />

          <Form onSubmit={handleSubmit}>
            <div className="d-flex justify-content-center mb-4">
              <div className="position-relative">
                <div
                  className="rounded-circle border"
                  style={{
                    width: "90px",
                    height: "90px",
                    overflow: "hidden",
                    backgroundColor: "#f8f9fa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {profileImagePreview ? (
                    <img
                      src={profileImagePreview}
                      alt="Profile Preview"
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                  ) : (
                    <Image size={28} color="#6c757d" />
                  )}
                </div>
                <Form.Label
                  htmlFor="profile-image-upload"
                  className="position-absolute bottom-0 end-0 bg-dark text-white rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: "28px",
                    height: "28px",
                    cursor: "pointer",
                    transform: "translate(10%, 10%)",
                  }}
                >
                  <CameraFill size={14} />
                </Form.Label>
                <Form.Control
                  type="file"
                  id="profile-image-upload"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="d-none"
                />
              </div>
            </div>

            {/* Row 1 */}
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold small">
                    First Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter First Name"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    isInvalid={!!formErrors.firstName}
                    className="py-2"
                  />
                  <Form.Control.Feedback type="invalid" className="small">
                    {formErrors.firstName}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold small">
                    Last Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Last Name"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    isInvalid={!!formErrors.lastName}
                    className="py-2"
                  />
                  <Form.Control.Feedback type="invalid" className="small">
                    {formErrors.lastName}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            {/* Row 2 */}
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold small">Role</Form.Label>
                  <Autocomplete
                    loading={loadingRoles}
                    disablePortal
                    options={roles}
                    getOptionLabel={(option) => option.name || ""}
                    value={
                      roles.find(
                        (role) => role.id === formData.selectedRoleId
                      ) || null
                    }
                    renderOption={(props, option) => (
                      <li {...props} className="p-2">
                        <div className="label-size m-0">{option.name}</div>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Role"
                        size="small"
                        error={!!formErrors.selectedRoleId}
                        helperText={formErrors.selectedRoleId}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            paddingRight: "9px !important",
                            backgroundColor: "white",
                          },
                        }}
                      />
                    )}
                    onChange={(event, newValue) => {
                      handleInputChange(
                        "selectedRoleId",
                        newValue ? newValue.id : null
                      );
                      handleInputChange(
                        "selectedRoleName",
                        newValue ? newValue.name : ""
                      );
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold small">
                    Join Date
                  </Form.Label>
                  <div className="custom-datepicker-wrapper">
                    <DatePicker
                      selected={formData.joined_at}
                      onChange={(date) => handleInputChange("joined_at", date)}
                      dateFormat="dd-MMM-yyyy"
                      className={`form-control py-2 ${
                        formErrors.joined_at ? "is-invalid" : ""
                      }`}
                      placeholderText="Select Date"
                      wrapperClassName="w-100"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                    />
                  </div>
                  {formErrors.joined_at && (
                    <div className="invalid-feedback d-block small">
                      {formErrors.joined_at}
                    </div>
                  )}
                </Form.Group>
              </div>
            </div>

            {/* Row 3 */}
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold small">
                    Mobile Number
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Enter Phone Number"
                    value={formData.mobileNumber}
                    onChange={(e) =>
                      handleInputChange("mobileNumber", e.target.value)
                    }
                    isInvalid={!!formErrors.mobileNumber}
                    className="py-2"
                  />
                  <Form.Control.Feedback type="invalid" className="small">
                    {formErrors.mobileNumber}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold small">
                    Birth Date
                  </Form.Label>
                  <DatePicker
                    selected={formData.birthDate}
                    onChange={(date) => handleInputChange("birthDate", date)}
                    dateFormat="dd-MMM-yyyy"
                    className={`form-control py-2 ${
                      formErrors.birthDate ? "is-invalid" : ""
                    }`}
                    placeholderText="Select Date"
                    wrapperClassName="w-100"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                  />
                  {formErrors.birthDate && (
                    <div className="invalid-feedback d-block small">
                      {formErrors.birthDate}
                    </div>
                  )}
                </Form.Group>
              </div>
            </div>

            {/* Row 4 */}
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold small">Gender</Form.Label>
                  <Autocomplete
                    disablePortal
                    options={genderOptions}
                    value={
                      formData.gender
                        ? formData.gender.charAt(0).toUpperCase() +
                          formData.gender.slice(1).toLowerCase()
                        : ""
                    }
                    renderOption={(props, option) => (
                      <li {...props} className="p-2">
                        <div className="label-size m-0">{option}</div>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Gender"
                        size="small"
                        error={!!formErrors.gender}
                        helperText={formErrors.gender}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                          },
                        }}
                      />
                    )}
                    onChange={(event, newValue) => {
                      handleInputChange(
                        "gender",
                        newValue ? newValue.toLowerCase() : ""
                      );
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option === value || value === ""
                    }
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold small">
                    Username
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter username"
                    value={formData.username}
                    onChange={(e) =>
                      handleInputChange("username", e.target.value)
                    }
                    isInvalid={!!formErrors.username}
                    className="py-2"
                  />
                  <Form.Control.Feedback type="invalid" className="small">
                    {formErrors.username}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            {/* Row 5 */}
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold small">
                    Category
                  </Form.Label>
                  <Autocomplete
                    disablePortal
                    freeSolo
                    options={operatorCategoriesList}
                    value={formData.category || ""}
                    renderOption={(props, option) => (
                      <li {...props} className="p-2">
                        <div className="label-size m-0">{option}</div>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Category"
                        size="small"
                        error={!!formErrors.category}
                        helperText={formErrors.category}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                          },
                        }}
                      />
                    )}
                    onInputChange={(event, newInputValue) => {
                      handleInputChange("category", newInputValue);
                    }}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold small">Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    isInvalid={!!formErrors.email}
                    className="py-2"
                  />
                  <Form.Control.Feedback type="invalid" className="small">
                    {formErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            {/* Row 6 */}
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold small">
                    Password{" "}
                    {isEditing && (
                      <span className="text-muted fw-normal">(Optional)</span>
                    )}
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      placeholder={
                        isEditing ? "New password" : "Enter Password"
                      }
                      value={formData.password}
                      onChange={(e) =>
                        handleInputChange("password", e.target.value)
                      }
                      isInvalid={!!formErrors.password}
                      className="py-2"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      className="py-2"
                    >
                      {showPassword ? (
                        <EyeSlashFill size={14} />
                      ) : (
                        <EyeFill size={14} />
                      )}
                    </Button>
                  </InputGroup>
                  <Form.Control.Feedback type="invalid" className="small">
                    {formErrors.password}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold small">
                    Confirm Password
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      placeholder={
                        isEditing ? "Confirm new" : "Confirm Password"
                      }
                      value={formData.passwordConfirmation}
                      onChange={(e) =>
                        handleInputChange(
                          "passwordConfirmation",
                          e.target.value
                        )
                      }
                      isInvalid={!!formErrors.passwordConfirmation}
                      className="py-2"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      className="py-2"
                    >
                      {showPassword ? (
                        <EyeSlashFill size={14} />
                      ) : (
                        <EyeFill size={14} />
                      )}
                    </Button>
                  </InputGroup>
                  <Form.Control.Feedback type="invalid" className="small">
                    {formErrors.passwordConfirmation}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            {/* Buttons */}
            <div className="d-flex justify-content-center gap-3 px-2 mt-4">
              <Button
                variant="light"
                onClick={onHide}
                className="px-5 border py-2"
                style={{ backgroundColor: "white" }}
              >
                Cancel
              </Button>
              <Button
                variant="dark"
                type="submit"
                className="px-5 py-2"
                style={{ backgroundColor: "black", borderColor: "black" }}
                disabled={loadingRoles}
              >
                {isEditing ? "Update" : "Save"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Addmember;
